import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { MaskReveal } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1497366754035-f200968a6e72?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBvZmZpY2UlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2Njk1NzkxfDA&ixlib=rb-4.1.0&q=85";

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen pt-28 md:pt-36 pb-16 px-6 md:px-12 overflow-hidden"
    >
      <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 items-end">
        {/* Left — kinetic headline */}
        <div className="lg:col-span-7 xl:col-span-8">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4, duration: 1 }}
            className="font-body text-xs md:text-sm tracking-[0.25em] uppercase text-[#ff3b30] mb-6 md:mb-10"
          >
            Verified European opportunities · Europe-focused recruitment
          </motion.p>

          <h1 className="font-head uppercase tracking-tighter leading-[0.86] text-[13vw] lg:text-[7.5vw] xl:text-[7rem]">
            <MaskReveal delay={0.15}>Connecting</MaskReveal>
            <MaskReveal delay={0.28}>
              <span className="text-[#a1a1aa]">European employers</span>
            </MaskReveal>
            <MaskReveal delay={0.41} className="flex items-baseline gap-4 flex-wrap">
              <span>with</span>
              <span className="font-accent italic font-500 lowercase text-[#ff3b30] tracking-normal">
                global
              </span>
              <span>talent.</span>
            </MaskReveal>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-10 md:mt-12 font-body text-base md:text-lg text-[#52525b] max-w-xl leading-relaxed"
          >
            Professional recruitment, international workforce sourcing and cross-border hiring
            support — built on verification and transparency.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <a
              href="#hire"
              data-testid="hero-hire"
              className="group inline-flex items-center gap-2 bg-[#ff3b30] hover:bg-[#ff5248] text-white font-head uppercase tracking-wide text-sm px-6 py-4 transition-colors duration-300"
            >
              Hire Talent
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-300" />
            </a>
            <a
              href="#find-job"
              data-testid="hero-find"
              className="inline-flex items-center gap-2 border border-[#e4e4e7] hover:border-[#ff3b30] font-head uppercase tracking-wide text-sm px-6 py-4 transition-colors duration-300"
            >
              Find a Job
            </a>
            <a
              href="#partner"
              data-testid="hero-partner"
              className="inline-flex items-center gap-2 border border-[#e4e4e7] hover:border-[#ff3b30] font-head uppercase tracking-wide text-sm px-6 py-4 transition-colors duration-300"
            >
              Become a Recruitment Partner
            </a>
          </motion.div>
        </div>

        {/* Right — clipped spotlight image with parallax */}
        <div className="lg:col-span-5 xl:col-span-4">
          <motion.div
            initial={{ clipPath: "inset(0 0 100% 0)" }}
            animate={{ clipPath: "inset(0 0 0% 0)" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.5 }}
            className="relative aspect-[3/4] overflow-hidden"
            data-testid="hero-image"
          >
            <motion.img
              src={HERO_IMG}
              alt="Modern workplace corridor"
              style={{ y: imgY, scale: imgScale }}
              className="absolute inset-0 w-full h-full object-cover img-grayscale"
            />
            <div className="absolute inset-0 ring-1 ring-inset ring-black/5" />
          </motion.div>
        </div>
      </div>

      {/* bottom strip */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.1, duration: 1 }}
        className="max-w-[1600px] mx-auto mt-16 md:mt-24 border-t border-[#e4e4e7] pt-6 flex flex-wrap gap-x-12 gap-y-4 font-body text-sm text-[#a1a1aa]"
      >
        <span>[ 01 ] Employers</span>
        <span>[ 02 ] Candidates</span>
        <span>[ 03 ] Recruitment partners</span>
        <span className="ml-auto text-[#52525b]">Verification-first ↓</span>
      </motion.div>
    </section>
  );
};

